import axios from 'axios';
import { API_BASE_URL } from '../config';

// Centralized API base URL
const API_URL = API_BASE_URL;

// Types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  user: {
    id: number;
    email: string;
    name: string;
    role: string;
    is_superuser: boolean;
    is_active: boolean;
  };
}

// Service for handling authentication
const authService = {
  // Login user
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    try {
      // Create form data for OAuth2 format
      const formData = new URLSearchParams();
      formData.append('username', credentials.email);  // OAuth2 uses 'username' field
      formData.append('password', credentials.password);
      
      const response = await axios.post<AuthResponse>(
        `${API_URL}/auth/login`,
        formData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        }
      );
      // Store token in local storage
      localStorage.setItem('auth_token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      return response.data;
    } catch (error) {
      throw new Error('Authentication failed. Please check your credentials.');
    }
  },

  // Logout user
  logout: (): void => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    // Optional: Call logout endpoint to invalidate token on server
    // axios.post(`${API_URL}/auth/logout`);
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem('auth_token');
    return !!token;
  },

  // Get current user
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      return JSON.parse(userStr);
    }
    return null;
  },

  // Get auth token
  getToken: (): string | null => {
    return localStorage.getItem('auth_token');
  },

  // Refresh token
  refreshToken: async (): Promise<AuthResponse> => {
    try {
      const response = await axios.post<AuthResponse>(
        `${API_URL}/auth/refresh`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          },
        }
      );
      localStorage.setItem('auth_token', response.data.access_token);
      return response.data;
    } catch (error) {
      // If token refresh fails, log the user out
      authService.logout();
      throw new Error('Session expired. Please login again.');
    }
  },
};

export default authService;
